import {createApi, fetchBaseQuery} from '@reduxjs/toolkit/query/react';
import urls from 'urls';
import {Assets, Ecard, GetAssetsRequestParams, RecipientMessages} from '~/QuickEcards/types/ecard';
import {FabricTemplate} from '~/common/fabric/types';
import {CuratedCard} from '~/QuickEcards/types/curatedCard';
import {store} from '~/QuickEcards/store';
import {UPDATE_GLOBAL_MESSAGE} from '~/CreateFabricGreetingCard/reducers/recipientsMessage';
import {UPDATE_SENDER} from '~/common/fabric/reducers/review/constants';

type AddCustomRecipientMessageRequestParams = {
  eventId: string;
  messages: RecipientMessages;
};

type GetCustomRecipientMessagesResponseBody = RecipientMessages & {
  data_layer: {[key: string]: unknown};
};

type RemoveCustomRecipientMessagesRequestParams = {
  eventId: string;
  guestId: string;
};

const handleRetrievedCardData = (data: Ecard) => {
  store.dispatch({
    type: UPDATE_GLOBAL_MESSAGE,
    payload: data.event_data.options.write.text_layer_back,
  });
  store.dispatch({
    type: UPDATE_SENDER,
    payload: data.event_data.event_host_name,
  });
  return data;
};

export const ecardApi = createApi({
  reducerPath: 'ecardApi',
  baseQuery: fetchBaseQuery({
    baseUrl: window.location.origin,
    credentials: 'include',
    headers: [
      ['Accept', 'application/json'],
      ['Content-Type', 'application/json'],
      ['X-CSRFToken', evite.cookie('csrftoken')],
    ].filter(([, v]) => !!v) as [string, string][],
  }),
  endpoints: (builder) => ({
    getFabricTemplate: builder.query<FabricTemplate, string>({
      query: (id) => ({
        method: 'GET',
        url: `/ajax/gallery/template/${id}`,
      }),
    }),
    getEcard: builder.query<Ecard, string>({
      query: (id) => ({
        method: 'GET',
        url: `/ajax/ecards/${id}`,
      }),
      transformResponse: handleRetrievedCardData,
    }),
    saveEcard: builder.mutation<Ecard, Ecard>({
      query: (ecard) => ({
        method: ecard.id ? 'PATCH' : 'POST',
        url: ecard.id ? `ajax/ecards/${ecard.id}/` : '/ajax/ecards/',
        body: JSON.stringify(ecard),
      }),
      transformResponse: handleRetrievedCardData,
    }),
    addCustomRecipientMessage: builder.mutation<void, AddCustomRecipientMessageRequestParams>({
      query: ({eventId, messages}) => ({
        method: 'POST',
        url: `/ajax/event/${eventId}/guestlist/add-custom-message/`,
        body: JSON.stringify(messages),
      }),
    }),
    getCustomRecipientMessages: builder.query<RecipientMessages, string>({
      query: (eventId) => ({
        method: 'GET',
        url: `/ajax/event/${eventId}/guestlist/get-custom-message/`,
      }),
      transformResponse: (response: GetCustomRecipientMessagesResponseBody) => {
        const data: Partial<RecipientMessages> = {...response};
        // This data_layer object is unused, so we will remove it and re-cast the rest as a regular RecipientMessages object
        delete data.data_layer;
        return data as RecipientMessages;
      },
    }),
    removeCustomRecipientMessage: builder.mutation<
      void,
      RemoveCustomRecipientMessagesRequestParams
    >({
      query: ({eventId, guestId}) => ({
        method: 'DELETE',
        url: `/ajax/event/${eventId}/guestlist/delete-custom-message/${guestId}/`,
      }),
    }),
    getAssets: builder.query<Assets, GetAssetsRequestParams>({
      query: ({assetGroups, primaryCategories, templateId}) => ({
        method: 'GET',
        url: urls.get('ajax_get_all_assets', {
          asset_groups: assetGroups,
          primary_categories: primaryCategories,
          template: templateId,
        }),
      }),
    }),
    getCuratedSet: builder.mutation<
      (CuratedCard | FabricTemplate)[],
      {category_id: string; page_size: number; page_offset: number}
    >({
      query: (props) => ({
        method: 'POST',
        url: '/ajax/ecards/templates-categories-matching-sets',
        body: JSON.stringify(props),
      }),
      transformResponse: (data: Record<string, CuratedCard>) => {
        const cardsDict: Record<string, CuratedCard> = {...data};
        const cards: (CuratedCard | FabricTemplate)[] = Object.values(cardsDict);
        cards.sort((a, b) => {
          if (a.id === window.fabricTemplate.id) return -1; // If "a" is the initial template, push it ahead
          if (b.id === window.fabricTemplate.id) return 1; // If "b" is the initial template, push it ahead
          if (!('popularity' in a)) return 1; // If for some reason "a" does not have popularity, push it down
          if (!('popularity' in b)) return -1; // If for some reason "b" does not have popularity, push it down
          return b.popularity - a.popularity;
        });
        if (!(window.fabricTemplate.id in cardsDict)) {
          cards.splice(0, 0, window.fabricTemplate);
        }
        return cards;
      },
    }),
  }),
});

export const {
  useLazyGetCustomRecipientMessagesQuery,
  useAddCustomRecipientMessageMutation,
  useRemoveCustomRecipientMessageMutation,
  useSaveEcardMutation,
  useLazyGetEcardQuery,
  useLazyGetAssetsQuery,
  useLazyGetFabricTemplateQuery,
  useGetCuratedSetMutation,
} = ecardApi;
